import {
  HEALTH_CHECKUP_REPORT,
  type HealthCheckupReportLengType,
} from './report';

import './FFRReportPreViewModel.scss';
import { Series } from '@models/Series';
import { ReactComponent as Aimedic } from 'img/aimedic.svg';

import torsoFront from 'img/torso-front.png';
import torsoLeft from 'img/torso-left.png';
import torsoRight from 'img/torso-right.png';
import torsoBack from 'img/torso-back.png';

import dayjs from 'dayjs';
import { ReactNode } from 'react';

export interface TItem {
  id: string | number;
  name: string;
  isSelected: boolean;
  color?: 'white' | 'yellow' | 'red' | 'aqua' | 'green' | null;
  renderType?: 'chip' | 'label' | 'text';
  addtionalClass?: string;
  isDisabled?: boolean;
}

export interface imageSet {
  one?: string;
  two?: string;
  three?: string;
  four?: string;
}

type FFRJsonData = {
  vecSelectedNodes?: number[];
  rcaFFR?: number;
  lcxFFR?: number;
  ladFFR?: number;
  [key: string]: unknown;
};

type VesselKey = 'LAD' | 'RCA' | 'LCX';

interface ReportPreViewModelProps {
  series: Series;
  ct_ffr?: FFRJsonData;
  language?: string | number;
  languageOptions?: TItem[];
  images: imageSet | undefined;
  reportRef: React.RefObject<HTMLDivElement>;
  onLanguageChange?: (updatedItems: TItem[]) => void;
}

const VESSELS: VesselKey[] = ['LAD', 'RCA', 'LCX'];

const toParagraphs = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);


const getFfrValue = (ct_ffr: FFRJsonData | undefined, vessel: VesselKey) => {
  const keyMap: Record<VesselKey, keyof FFRJsonData> = {
    LAD: 'ladFFR',
    RCA: 'rcaFFR',
    LCX: 'lcxFFR',
  };

  const value = ct_ffr?.[keyMap[vessel]];

  if (typeof value !== 'number') return '-';

  return value.toFixed(2);
};

const getCommentKey = (value: string) => {
  const num = Number(value);
  if (Number.isNaN(num)) return 'NORMAL';
  if (num <= 0.8) return 'LOW';
  if (num <= 0.85) return 'BORDERLINE';
  return 'NORMAL';
};

function InfoSection({
  title,
  children,
  language,
}: {
  title: string;
  children: ReactNode;
  language?: HealthCheckupReportLengType;
}) {
  return (
    <section className="report-section">
      <div className="section-inner">
        <span className="section-title">{title}</span>
        <div className="section-border">
          <svg
            className="section-ecg"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 670 34"
          >
            <path
              d="M0 16.5H18.5783L21.6747 14L25.8032 21L29.9317 1L33.5442 33L37.6727 12.5L40.7691 19L43.3494 14L44.3815 20L47.4779 16.5H670"
              stroke="#A63F57"
              strokeWidth="2"
            />
          </svg>

          <div className="section-content" lang={language}>
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

const datetimeFormatter = (
  datetime: string | null | undefined,
  format: string,
) => {
  if (!datetime) return '';
  return dayjs(datetime).isValid() ? dayjs(datetime).format(format) : '';
};

export default function ReportPreViewModel({
  series,
  ct_ffr,
  language = 'en',
  languageOptions = [],
  images,
  reportRef,
  onLanguageChange,
}: ReportPreViewModelProps) {
  const selectedLanguage = 'en'; //language as HealthCheckupReportLengType;
  const content =
    HEALTH_CHECKUP_REPORT[selectedLanguage] ?? HEALTH_CHECKUP_REPORT.en;

  const torsoImages = [torsoFront,torsoBack, torsoLeft, torsoRight, ];

  const { PATIENT, SECTIONS, CTFFR_RESULT, DATE_FORMAT } = content;

  const {
    patientId,
    patientAge: age,
    patientBirthdate,
    patientSex: patientSexRaw,
    patientName,
  } = series.patient;
  const ageStr = String(age ?? '').replace(/\D/g, '');
  const parsedAge = ageStr ? `${parseInt(ageStr, 10)}${PATIENT.AGE}` : null;
  const parsedBirthDate = datetimeFormatter(patientBirthdate, DATE_FORMAT);
  const scanDate = datetimeFormatter(series.studyDate, DATE_FORMAT);

  const patientSex =
    patientSexRaw?.toUpperCase() === 'M'
      ? PATIENT.MALE
      : patientSexRaw?.toUpperCase() === 'F'
        ? PATIENT.FEMALE
        : '';

  const patientInfo = [patientSex, parsedBirthDate, parsedAge]
    .filter(Boolean)
    .join('/');

  const handleLanguageChange = (id: string) => {
    if (!onLanguageChange) return;
    onLanguageChange(
      languageOptions.map((item) => ({
        ...item,
        isSelected: String(item.id) === id,
      })),
    );
  };

  return (
    <div className="report-preview-shell">
      <div className="report-preview-scroll">
        <div ref={reportRef} className="report-pages">
          <article className="report-page">
            <Aimedic className="report-logo" />
            <h1 className="report-main-title">CT-FFR</h1>
            <h2 className="report-title">{content.REPORT_TITLE}</h2>

            <div className="report-meta">
              <strong>
                {content.SCAN_DATE} {scanDate}
              </strong>

              <div className="patient-meta">
                <strong>
                  {PATIENT.ID} {patientId}
                </strong>
                <strong>
                  {`${patientName || 'UNKNOWN'}${patientInfo ? ` (${patientInfo})` : ''}`}
                </strong>
              </div>
            </div>

            <InfoSection
              title={SECTIONS.WHAT_IS_CORONARY.TITLE}
              language={selectedLanguage}
            >
              {toParagraphs(SECTIONS.WHAT_IS_CORONARY.DESC).map((text) => (
                <p key={text}>{text}</p>
              ))}
            </InfoSection>

            <InfoSection
              title={SECTIONS.WHAT_IS_FFR.TITLE}
              language={selectedLanguage}
            >
              {toParagraphs(SECTIONS.WHAT_IS_FFR.DESC).map((text) => (
                <p key={text}>{text}</p>
              ))}
            </InfoSection>

            <InfoSection
              title={SECTIONS.WHAT_IS_CTFFR.TITLE}
              language={selectedLanguage}
            >
              {toParagraphs(SECTIONS.WHAT_IS_CTFFR.DESC).map((text) => (
                <p key={text}>{text}</p>
              ))}
            </InfoSection>
          </article>

          <article className="report-page">
            <InfoSection title={CTFFR_RESULT.TITLE} language={selectedLanguage}>
              {CTFFR_RESULT.DESCRIPTION.map((text) => (
                <p key={text}>{text}</p>
              ))}

              <ul>
                {CTFFR_RESULT.COLOR_GUIDE.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>

              <div className="ctffr-result-area">
                <div className="ctffr-images">
                  {(images
                    ? [images.one, images.two, images.three, images.four]
                    : Array(4).fill(undefined)
                  ).map((src, index) => (
                    <div className="ctffr-image-box" key={index}>
                      {src ? (
                        <>
                          <img
                            className="ctffr-result-image"
                            src={src}
                            alt={`CT-FFR result ${index + 1}`}
                          />

                          <img
                            className="ctffr-body-image"
                            src={torsoImages[index]}
                            alt=""
                          />
                        </>
                      ) : (
                        <div className="empty">CT-FFR Image</div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="ctffr-scale">
                  <strong>CT-FFR</strong>
                  <div className="scale-bar" />
                  {[1.0, 0.9, 0.8, 0.7, 0.6, 0.5].map((value) => (
                    <span key={value}>{value.toFixed(1)}</span>
                  ))}
                </div>
              </div>

              <p className="table-title">{CTFFR_RESULT.TABLE.TITLE}</p>

              <table className="ctffr-table">
                <thead>
                  <tr>
                    <th>{CTFFR_RESULT.TABLE.HEADER.VESSEL}</th>
                    <th>{CTFFR_RESULT.TABLE.HEADER.VALUE}</th>
                    <th>{CTFFR_RESULT.TABLE.HEADER.COMMENT}</th>
                  </tr>
                </thead>

                <tbody>
                  {VESSELS.map((vessel) => {
                    const value = getFfrValue(ct_ffr, vessel);
                    const commentKey = getCommentKey(value);

                    return (
                      <tr key={vessel}>
                        <td>{CTFFR_RESULT.TABLE.VESSELS[vessel]}</td>
                        <td>{value}</td>
                        <td>{CTFFR_RESULT.TABLE.COMMENT[commentKey]}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </InfoSection>

            <ul className="report-footnote">
              {CTFFR_RESULT.FOOTNOTE.map((text) => (
                <li key={text}>{text}</li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </div>
  );
}
